import type {UseFormReturn} from 'react-hook-form';
import {useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormRequiredLegend,
} from '@/components/ui/form';
import {ToggleGroup, ToggleGroupItem} from '@/components/ui/toggle-group';
import type {MedicationForm} from '@/services/graphqlHooks';
import {TreatmentType} from '@/services/graphqlHooks';

import type {ResidentMedicationBasicFormValues} from '../utils/medicationFormInputs';
import type {DosageInputProps} from './DosageInput';
import MedicationAsNeededFields from './resident-medication-form-fields/MedicationAsNeededFields';
import MedicationIdentityFields from './resident-medication-form-fields/MedicationIdentityFields';
import MedicationNotesFields from './resident-medication-form-fields/MedicationNotesFields';
import MedicationScheduleFields from './resident-medication-form-fields/MedicationScheduleFields';
import ResidentInput from './ResidentInput';

export type ResidentMedicationBaseFormFieldsProps = {
  form: UseFormReturn<ResidentMedicationBasicFormValues>;
  showResident?: boolean;
  onAddDosageClick?: DosageInputProps['onAddDosageClick'];
  onDeleteDosageClick?: DosageInputProps['onDeleteDosageClick'];
  onEditDosageClick?: DosageInputProps['onEditDosageClick'];
};

function ResidentMedicationBaseFormFields({
  form,
  showResident,
  onAddDosageClick,
  onEditDosageClick,
  onDeleteDosageClick,
}: ResidentMedicationBaseFormFieldsProps) {
  const {t} = useTranslation();

  const selectedTreatmentType =
    useWatch({
      control: form.control,
      name: 'treatmentType',
    }) || TreatmentType.Continuous;
  const selectedMedicationForm = useWatch({
    control: form.control,
    name: 'form',
  }) as MedicationForm | undefined;
  const isTemporary =
    (selectedTreatmentType as TreatmentType) === TreatmentType.Temporary;
  const isAsNeeded =
    (selectedTreatmentType as TreatmentType) === TreatmentType.AsNeeded;

  return (
    <>
      <FormRequiredLegend />

      {showResident && (
        <FormField
          control={form.control}
          name="residentId"
          render={({field}) => (
            <FormItem>
              <FormLabel required>{t('Resident')}</FormLabel>
              <FormControl>
                <ResidentInput value={field.value} onChange={field.onChange} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      <MedicationIdentityFields form={form} />

      <FormField
        control={form.control}
        name="treatmentType"
        render={({field}) => (
          <FormItem>
            <FormLabel>{t('Treatment type')}</FormLabel>
            <FormControl>
              <ToggleGroup
                size="small"
                type="single"
                itemVariant="white"
                onValueChange={value => {
                  if (!value) return;

                  field.onChange(value);
                }}
                value={field.value || TreatmentType.Continuous}
              >
                <ToggleGroupItem value={TreatmentType.Continuous}>
                  {t('Continuous')}
                </ToggleGroupItem>
                <ToggleGroupItem value={TreatmentType.Temporary}>
                  {t('Temporary')}
                </ToggleGroupItem>
                <ToggleGroupItem value={TreatmentType.AsNeeded}>
                  {t('As needed')}
                </ToggleGroupItem>
              </ToggleGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {isAsNeeded && (
        <MedicationAsNeededFields
          form={form}
          selectedMedicationForm={selectedMedicationForm}
        />
      )}
      {!isAsNeeded && (
        <MedicationScheduleFields
          form={form}
          isTemporary={isTemporary}
          onAddDosageClick={onAddDosageClick}
          onEditDosageClick={onEditDosageClick}
          onDeleteDosageClick={onDeleteDosageClick}
        />
      )}

      <MedicationNotesFields form={form} />
    </>
  );
}

export default ResidentMedicationBaseFormFields;
